import { type CreateInstancesBody, createInstances, searchContent } from '@echo/api-client/onearly';
import type { StrongerMemoryTypes } from '../config/environment';
import { localDayKey, timeZoneOffsetMinutes } from '../domain/day';
import { EXERCISE_ORDER, type ExerciseKind } from '../domain/exercise';
import { OnEarlyRequestError } from './errors';
import { type ContentGraph, childrenOf, includedLineagesOf, jsonValueOf, readGraph } from './graph';
import {
  EXERCISE_KIND,
  SM_KIND,
  type SmAnswer,
  type SmAttemptValue,
  smAnswerValueSchema,
  smAttemptValueSchema,
  smSessionValueSchema,
} from './schemas';
import { performAsFor, sameScope, type WriteScope, writtenScopeOf } from './scope';
import { shareResultsWithGroup } from './share';

/**
 * What a person did: one `SM DaySession` per local day, CONTAINING one `SM Exercise Attempt`
 * per exercise done, each math attempt CONTAINING its answers and INCLUDING the sheet or
 * prompt it used. Private to the participant; Core's search only returns what the caller
 * may read, so "my sessions" needs no author filter. Written as a member of the group where
 * the person is in it (`scope.ts`); a day can hold a row in each scope.
 */
export interface Attempt {
  uuid: string;
  lineageUuid: string;
  kind: ExerciseKind;
  value: SmAttemptValue;
  /** Lineage uuids of the library items the attempt used. */
  includes: string[];
  answers: SmAnswer[];
}

export interface DaySession {
  uuid: string;
  lineageUuid: string;
  /** The local day the row was created for — older rows carry the UTC day (see `attemptDay`). */
  date: string;
  /** IANA zone of the device that created it, when recorded. */
  timeZone?: string;
  /**
   * Who wrote the row, from its `AUTHORED_BY` edge: an attempt joins only a row written in its
   * own scope, since Core lets only the exact author add to a node. Undefined when unknown.
   */
  writtenAs?: WriteScope;
  attempts: Attempt[];
}

/**
 * The newest sessions without their attempts and answers: enough to know which session a day
 * already has, for a small part of `loadSessions`' download. Resending a saved exercise needs
 * no more, and on a slow line the whole history was the download that kept failing.
 */
export async function loadSessionIndex(
  types: StrongerMemoryTypes,
  first = 14,
): Promise<DaySession[]> {
  const response = await searchContent({
    rootContentTypeUuids: [types.session],
    maxDepth: 0,
    first,
  });
  if (response.status !== 200)
    throw new Error(`OnEarly: sessions search failed (${response.status})`);
  return sessionsFromGraph(readGraph(response.data), types);
}

export async function loadSessions(types: StrongerMemoryTypes, first = 100): Promise<DaySession[]> {
  const response = await searchContent({
    rootContentTypeUuids: [types.session],
    maxDepth: 3,
    first,
  });
  if (response.status !== 200)
    throw new Error(`OnEarly: sessions search failed (${response.status})`);
  return sessionsFromGraph(readGraph(response.data), types);
}

export function sessionsFromGraph(graph: ContentGraph, types: StrongerMemoryTypes): DaySession[] {
  const sessions: DaySession[] = [];
  for (const root of graph.roots) {
    const node = graph.nodes.get(root);
    if (!node || node.contentTypeUuid !== types.session) continue;
    const value = smSessionValueSchema.safeParse(jsonValueOf(node));
    if (!value.success) continue;
    const attempts: Attempt[] = [];
    for (const child of childrenOf(graph, node.uuid)) {
      if (child.contentTypeUuid !== types.attempt) continue;
      const attempt = smAttemptValueSchema.safeParse(jsonValueOf(child));
      if (!attempt.success) continue;
      const answers = childrenOf(graph, child.uuid)
        .filter((grandchild) => grandchild.contentTypeUuid === types.answer)
        .flatMap((grandchild) => {
          const parsed = smAnswerValueSchema.safeParse(jsonValueOf(grandchild));
          return parsed.success ? [parsed.data] : [];
        })
        .sort((x, y) => x.index - y.index);
      attempts.push({
        uuid: child.uuid,
        lineageUuid: child.lineageUuid,
        kind: EXERCISE_KIND[attempt.data.kind],
        value: attempt.data,
        includes: includedLineagesOf(graph, child.uuid),
        answers,
      });
    }
    const writtenAs = writtenScopeOf(graph, node.uuid);
    sessions.push({
      uuid: node.uuid,
      lineageUuid: node.lineageUuid,
      date: value.data.date,
      ...(value.data.timeZone ? { timeZone: value.data.timeZone } : {}),
      ...(writtenAs ? { writtenAs } : {}),
      attempts,
    });
  }
  // Newest first. A day can have more than one session row (two devices offline, or rows
  // written as the person and as a member of the group); everything below merges them by date.
  return sessions.sort((x, y) => (x.date < y.date ? 1 : x.date > y.date ? -1 : 0));
}

/**
 * The session row for `date` written in `scope` — where a new attempt of that day in that scope
 * goes. None when the day has no such row: the attempt then starts one.
 */
export function sessionOn(
  sessions: readonly DaySession[],
  date: string,
  scope: WriteScope,
): DaySession | undefined {
  return sessions.find((session) => session.date === date && sameScope(session.writtenAs, scope));
}

/**
 * The local calendar day an attempt was finished on: its timestamp in the session's time
 * zone. Sessions used to be dated by the UTC day, so a routine finished at 09:00
 * in Brisbane sat inside the previous day's row; reading the attempt's own day counts it
 * right without touching the data. Without a timestamp or a zone, the row's date stands.
 */
export function attemptDay(session: DaySession, attempt: Attempt): string {
  const { completedAt } = attempt.value;
  if (!completedAt || !session.timeZone) return session.date;
  const instant = new Date(completedAt);
  if (Number.isNaN(instant.getTime())) return session.date;
  // A zone the engine cannot resolve: the device's own offset — the two share a zone in practice.
  const offset = timeZoneOffsetMinutes(session.timeZone, instant) ?? instant.getTimezoneOffset();
  return localDayKey(instant, offset);
}

/** Every attempt finished on `date`, whichever session row holds it. */
export function attemptsOn(sessions: readonly DaySession[], date: string): Attempt[] {
  const found: Attempt[] = [];
  for (const session of sessions) {
    for (const attempt of session.attempts) {
      if (attemptDay(session, attempt) === date) found.push(attempt);
    }
  }
  return found;
}

/**
 * What a finished exercise was about, as the person named it: the title typed for their own
 * book or paper, or the writing category they chose. Undefined when nothing was named (a
 * workbook item, or an older attempt), so the caller falls back to the workbook title.
 */
export function doneTitleOn(
  sessions: readonly DaySession[],
  date: string,
  kind: ExerciseKind,
): string | undefined {
  return attemptsOn(sessions, date).find(
    (attempt) => attempt.kind === kind && attempt.value.state === 'done',
  )?.value.sourceTitle;
}

/**
 * The first local day with any activity — "Member since" and the start of the log. Rows are
 * considered too: a day whose only attempt is still open has begun even if nothing is done.
 */
export function earliestDay(sessions: readonly DaySession[]): string | undefined {
  let earliest: string | undefined;
  const consider = (day: string) => {
    if (earliest === undefined || day < earliest) earliest = day;
  };
  for (const session of sessions) {
    consider(session.date);
    for (const attempt of session.attempts) consider(attemptDay(session, attempt));
  }
  return earliest;
}

/** Exercises with a done attempt on `date`. */
export function doneKindsOn(sessions: readonly DaySession[], date: string): Set<ExerciseKind> {
  const done = new Set<ExerciseKind>();
  for (const attempt of attemptsOn(sessions, date)) {
    if (attempt.value.state === 'done') done.add(attempt.kind);
  }
  return done;
}

/** Library items ever used for `kind` — what "pick for me" skips. */
export function usedLineages(sessions: readonly DaySession[], kind: ExerciseKind): Set<string> {
  const used = new Set<string>();
  for (const session of sessions) {
    for (const attempt of session.attempts) {
      if (attempt.kind === kind) for (const lineage of attempt.includes) used.add(lineage);
    }
  }
  return used;
}

/** Days on which the whole routine (all three exercises) was done — the streak's unit. */
export function completedDates(sessions: readonly DaySession[]): string[] {
  const byDate = new Map<string, Set<ExerciseKind>>();
  for (const session of sessions) {
    for (const attempt of session.attempts) {
      if (attempt.value.state !== 'done') continue;
      const day = attemptDay(session, attempt);
      const kinds = byDate.get(day) ?? new Set<ExerciseKind>();
      kinds.add(attempt.kind);
      byDate.set(day, kinds);
    }
  }
  return [...byDate.entries()]
    .filter(([, kinds]) => EXERCISE_ORDER.every((kind) => kinds.has(kind)))
    .map(([date]) => date)
    .sort();
}

export interface RecordAttemptInput {
  kind: ExerciseKind;
  /** Participant-local ISO date; the session it belongs to. */
  date: string;
  timeZone: string;
  startedAt: string;
  completedAt: string;
  activeSeconds: number;
  source: 'workbook' | 'own' | 'other';
  sourceTitle?: string;
  /** Lineage uuid of the sheet or prompt used, if any. */
  includesLineageUuid?: string;
  /** Quick math kept going past a sheet's end: the further sheets it went through. */
  alsoIncludesLineageUuids?: string[];
  answers?: SmAnswer[];
  /** Writing: the File content node of the page photo, already uploaded in `scope`. */
  photoFileUuid?: string;
  groupActorUuid?: string;
  /** Who the attempt (and a new session row) is written as. */
  scope: WriteScope;
  /**
   * The person the exercise is about, as Core's subject: what a leader's mark about them is
   * found by too, so everything about one person can be read by subject whoever wrote it.
   */
  subjectUuid?: string;
  /** Today's session written in `scope`, when one already exists — the attempt goes inside it. */
  sessionUuid?: string;
  /** That session's lineage, for sharing the day's results with the group again. */
  sessionLineageUuid?: string;
}

/** The `POST /api/v1/content/instances` body for one finished exercise. */
export function attemptBody(
  types: StrongerMemoryTypes,
  input: RecordAttemptInput,
): CreateInstancesBody {
  const attempt: SmAttemptValue = {
    kind: SM_KIND[input.kind],
    state: 'done',
    source: input.source,
    ...(input.sourceTitle ? { sourceTitle: input.sourceTitle } : {}),
    startedAt: input.startedAt,
    completedAt: input.completedAt,
    activeSeconds: Math.max(0, Math.round(input.activeSeconds)),
    markedBy: 'self',
    ...(input.groupActorUuid ? { groupActorUuid: input.groupActorUuid } : {}),
  };
  const about = input.subjectUuid ? { subjectUuid: input.subjectUuid } : {};
  const nodes: CreateInstancesBody['nodes'] = [];
  const contains: NonNullable<CreateInstancesBody['contains']> = [];
  const includes: NonNullable<CreateInstancesBody['includes']> = [];
  let sessionRef = input.sessionUuid;
  if (!sessionRef) {
    nodes.push({
      clientId: 'session',
      contentTypeUuid: types.session,
      occurredAt: input.startedAt,
      ...about,
      value: {
        jsonValue: {
          date: input.date,
          timeZone: input.timeZone,
          ...(input.groupActorUuid ? { groupActorUuid: input.groupActorUuid } : {}),
        },
      },
    });
    sessionRef = 'session';
  }
  nodes.push({
    clientId: 'attempt',
    contentTypeUuid: types.attempt,
    occurredAt: input.completedAt,
    ...about,
    value: { jsonValue: attempt },
  });
  contains.push({ from: sessionRef, to: 'attempt', position: EXERCISE_ORDER.indexOf(input.kind) });
  // Every sheet gone through counts as used, so "pick for me" doesn't offer it again.
  const included = new Set(
    [input.includesLineageUuid, ...(input.alsoIncludesLineageUuids ?? [])].filter(
      (lineage): lineage is string => lineage !== undefined,
    ),
  );
  for (const lineage of included) includes.push({ from: 'attempt', to: lineage });
  // The handwriting photo hangs off the attempt like the answers do, so it is the person's
  // alone (never in the shared summary) and comes back with the session.
  if (input.photoFileUuid) contains.push({ from: 'attempt', to: input.photoFileUuid, position: 0 });
  for (const answer of input.answers ?? []) {
    const clientId = `answer-${answer.index}`;
    nodes.push({
      clientId,
      contentTypeUuid: types.answer,
      occurredAt: input.completedAt,
      ...about,
      value: { jsonValue: answer },
    });
    contains.push({ from: 'attempt', to: clientId, position: answer.index });
  }
  const performAs = performAsFor(input.scope);
  return {
    nodes,
    contains,
    ...(includes.length ? { includes } : {}),
    ...(performAs ? { performAs } : {}),
  };
}

export interface RecordedAttempt {
  sessionUuid: string;
  /** The day's session lineage, when Core named it or the caller passed it. */
  sessionLineageUuid?: string;
  attemptUuid: string;
}

export async function recordAttempt(
  types: StrongerMemoryTypes,
  input: RecordAttemptInput,
): Promise<RecordedAttempt> {
  const response = await createInstances(attemptBody(types, input));
  if (response.status !== 200)
    throw new OnEarlyRequestError(
      `OnEarly: saving the exercise failed (${response.status})`,
      response.status,
    );
  const created = response.data.created;
  const attempt = created.find((node) => node.clientId === 'attempt');
  const session = created.find((node) => node.clientId === 'session');
  const sessionLineageUuid = session?.lineageUuid ?? input.sessionLineageUuid;
  // Results go to the group's admins, as their author: the day's whole session each time, with
  // what it contains, so a share that failed for an earlier exercise is made by the next one.
  // Best effort: the exercise is saved either way, and Core keeps it for its owner.
  const performAs = performAsFor(input.scope);
  const shared = sessionLineageUuid ?? attempt?.lineageUuid;
  if (performAs && shared) {
    await shareResultsWithGroup([shared], performAs.actorUuid, performAs).catch(() => undefined);
  }
  return {
    sessionUuid: session?.uuid ?? input.sessionUuid ?? '',
    ...(sessionLineageUuid ? { sessionLineageUuid } : {}),
    attemptUuid: attempt?.uuid ?? '',
  };
}
